/* eslint-disable dot-notation */
import React, { useEffect, useRef, useState } from 'react';
import cookies from 'js-cookie';
import config from 'Config';
import getConfig from 'next/config';
import { DidomiSDK, CurrentUserStatusValue } from '@didomi/react';
import { useOnClickOutside } from 'Src/hooks/use-on-click-outside/useOnClickOutside';
import { DIDOMI_VENDOR_CONSENT_COOKIE } from 'Tracking/constants';
import { getCookieValue } from 'Tracking/cookies/getCookieValue';
type DidomiBannerProps = {
  optMode: OptMode;
};
const cookie = cookies.withConverter({
  write: function (value) {
    return value.replace('%2C', ',');
  },
});

const generateVendorsConsentString = (value: CurrentUserStatusValue<string>) => {
  const enabledVendorsIds = Object.values(value)
    .filter(({ enabled }: any) => enabled)
    .map(({ id }: any) => id);
  return enabledVendorsIds.join(',');
};

// eslint-disable-next-line no-promise-executor-return
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const DidomiBanner = ({ optMode }: DidomiBannerProps) => {
  const didomi = useRef();
  const didomiNotice = useRef(null);
  const [didomiConfig, setDidomiConfig] = useState();
  const { publicRuntimeConfig } = getConfig();
  const apiKey =
    publicRuntimeConfig.NEXT_PUBLIC_DIDOMI_API_KEY || config.get('tracking.didomi.apiKey');

  if (!apiKey) {
    console.error('Didomi apiKey is not defined');
  }

  // Build consent URLs dynamically from current domain
  // This allows the SDK to be served via reverse proxy to bypass adblockers
  // See: https://developers.didomi.io/cmp/web-sdk/serve-didomi-assets-from-your-domain
  const [consentUrls, setConsentUrls] = useState(null);
  useEffect(() => {
    const hostname = window.location.hostname;
    const baseUrl = `https://${hostname}`;
    setConsentUrls({
      sdkPath: `${baseUrl}/consent/`,
      apiPath: `${baseUrl}/consent/api`,
    });
  }, []);
  useEffect(() => {
    if (!consentUrls) {
      return;
    }

    const organizationUserId = getCookieValue('_ga');
    setDidomiConfig({
      organizationUserId,
      externalConsent: {
        enabled: true,
      },
      apiPath: consentUrls.apiPath,
    });
  }, [consentUrls]);

  const agreeAndHideNotice = () => {
    if (didomi.current && optMode === 'opt-out') {
      didomi.current.setUserAgreeToAll();
      didomi.current.notice.hide();
    }
  };

  useOnClickOutside(didomiNotice, agreeAndHideNotice);

  const setUp = (didomiSDK) => {
    didomi.current = didomiSDK;

    if (!getCookieValue(DIDOMI_VENDOR_CONSENT_COOKIE)) {
      cookie.set(
        DIDOMI_VENDOR_CONSENT_COOKIE,
        generateVendorsConsentString(didomiSDK.getCurrentUserStatus().vendors),
        {
          expires: 180,
        },
      );
    }
  };

  const setUpNoticeRef = async () => {
    let didomiNoticeElement = document.getElementById('didomi-notice');

    if (!didomiNoticeElement) {
      await sleep(1000).then(async () => {
        didomiNoticeElement = document.getElementById('didomi-notice');
      });
    }

    didomiNotice.current = didomiNoticeElement;
  };

  const updateVendorCookie = () => {
    if (didomi.current) {
      cookie.set(
        DIDOMI_VENDOR_CONSENT_COOKIE,
        generateVendorsConsentString(didomi.current.getCurrentUserStatus().vendors),
        {
          expires: 180,
        },
      );
    }
  };

  if (!didomiConfig || !consentUrls) {
    return null;
  }

  return (
    <DidomiSDK
      apiKey={apiKey}
      sdkPath={consentUrls.sdkPath}
      config={didomiConfig}
      onReady={setUp}
      onNoticeShown={setUpNoticeRef}
      onConsentChanged={updateVendorCookie}
    />
  );
};
